"use client";

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Casestudyshowcase() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const tiles = [{
    id: "tile-a",
    route: "/FoodProcessingPage",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.27.58.png?v=4mghy8zk9sr",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.27.58.png?v=i2e8306iwjg-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.27.58.png?v=73xw57s2pbj-wvc-sizes",
    alt: "Renderização técnica de sistemas de engenharia industrial Eurocontrol",
    caption: "PROCESSAMENTO DE ALIMENTOS",
    className: "md:col-span-4 md:row-span-2 aspect-[4/3] md:aspect-auto md:min-h-[560px]",
    isHero: true
  }, {
    id: "tile-b",
    route: "/MeatAndPoultryPage",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.44.png?v=vzwpx2c7rxl",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.44.png?v=og25hf0z97m-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.44.png?v=amlxacww6x-wvc-sizes",
    alt: "Interface de controlo de precisão para pulverização industrial",
    caption: "CARNES & AVES",
    className: "md:col-span-2 aspect-[4/3]",
    isHero: false
  }, {
    id: "tile-c",
    route: "/AnimalFeedsPage",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.49.png?v=g29e1o4146",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.49.png?v=kcgx8hlejbr-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.49.png?v=6yewgcnb5by-wvc-sizes",
    alt: "Close-up de bicos pulverizadores de alta performance",
    caption: "RAÇÕES DE ANIMAIS",
    className: "md:col-span-2 aspect-[4/3]",
    isHero: false
  }, {
    id: "tile-d",
    route: "/SnackIndustryPage",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.58.png?v=7xgffcm8swx",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.58.png?v=rrsa6hi0n9a-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.58.png?v=oc9cg0nob-wvc-sizes",
    alt: "Visualização de aplicações industriais em ambiente fabril",
    caption: "INDUSTRIA DE SNACKS",
    className: "md:col-span-3 aspect-[16/9]",
    isHero: false
  }, {
    id: "tile-e",
    route: "/WinePage",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.48.03.png?v=8mctqusci3c",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.48.03.png?v=o7m661farp-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.48.03.png?v=k65e7a4e1y7-wvc-sizes",
    alt: "Dashboard de eficiência e sustentabilidade em processos",
    caption: "VINHO",
    className: "md:col-span-3 aspect-[16/9]",
    isHero: false
  }];
  useEffect(() => {
    if (!sectionRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add({
        reduce: "(prefers-reduced-motion: reduce)",
        motion: "(prefers-reduced-motion: no-preference)"
      }, context => {
        const conditions = context.conditions as {
          reduce: boolean;
          motion: boolean;
        };
        if (conditions.reduce) {
          gsap.set("[data-reveal='intro'], [data-reveal='tile']", {
            opacity: 1,
            y: 0,
            clearProps: "all"
          });
          return;
        }
        gsap.from("[data-reveal='intro']", {
          opacity: 0,
          y: 24,
          duration: 0.7,
          ease: "cubic-bezier(0.22,0.61,0.36,1)",
          stagger: 0.08,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 82%"
          }
        });
        gsap.from("[data-reveal='tile']", {
          opacity: 0,
          y: 32,
          duration: 0.7,
          ease: "cubic-bezier(0.22,0.61,0.36,1)",
          stagger: 0.1,
          scrollTrigger: {
            trigger: "[data-grid='bento']",
            start: "top 85%"
          }
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return <section data-section-id="31406" ref={sectionRef} id="casestudyshowcase" data-nav="dark" className="bg-background text-foreground relative overflow-hidden cursor-pointer" style={{
    paddingBlock: "clamp(120px, 14vw, 200px)"
  }}>
      <div className="mx-auto w-full max-w-[1360px]" style={{
      paddingInline: "clamp(20px, 4vw, 56px)"
    }}>
        <div data-grid="bento" className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-6">
          {tiles.map((tile, i) => <Link key={tile.id} to={tile.route || "#"} data-index={i} className={`block ${tile.className}`}>
              <motion.div data-index={i} data-reveal="tile" whileHover={prefersReducedMotion ? undefined : "hover"} whileFocus={prefersReducedMotion ? undefined : "hover"} initial="rest" animate="rest" tabIndex={0} className={`h-full w-full group relative overflow-hidden rounded-xl border border-border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-4 focus-visible:ring-offset-background cursor-pointer ${tile.className}`} transition={{
            duration: 0.4,
            ease: [0.22, 0.61, 0.36, 1]
          }} variants={{
            rest: {},
            hover: {}
          }}>
                <motion.img src={tile.src} data-wvc-srcset={tile.srcset} data-wvc-sizes={tile.sizes} alt={tile.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" variants={{
              rest: {
                scale: 1
              },
              hover: {
                scale: 1.03
              }
            }} transition={{
              duration: 0.5,
              ease: [0.22, 0.61, 0.36, 1]
            }} />

                {tile.isHero && <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
              background: "linear-gradient(115deg, hsla(215, 80%, 20%, 0.22) 0%, hsla(215, 90%, 45%, 0.15) 30%, hsla(240, 8%, 4%, 0) 65%)"
            }} />}

                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5" style={{
              background: "linear-gradient(to top, hsla(240, 8%, 4%, 0.85) 0%, hsla(240, 8%, 4%, 0.35) 45%, hsla(240, 8%, 4%, 0) 100%)"
            }} />

                <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-xl border" variants={{
              rest: {
                opacity: 0
              },
              hover: {
                opacity: 1
              }
            }} transition={{
              duration: 0.4,
              ease: [0.22, 0.61, 0.36, 1]
            }} style={{
              borderColor: "hsla(215, 90%, 45%, 0.5)"
            }} />

                <motion.div variants={{
              rest: {
                y: 0
              },
              hover: {
                y: -4
              }
            }} transition={{
              duration: 0.4,
              ease: [0.22, 0.61, 0.36, 1]
            }} className="absolute bottom-5 left-5 z-10">
                  <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 text-white font-semibold backdrop-blur-md" style={{
                padding: "10px 16px",
                backgroundColor: "hsla(240, 8%, 4%, 0.62)",
                fontSize: "clamp(0.9375rem, 1.15vw, 1.125rem)",
                letterSpacing: "0.04em",
                lineHeight: 1.35,
                textShadow: "0 1px 3px hsla(240, 8%, 4%, 0.65)"
              }}>
                    <span>{tile.caption}</span>
                  </div>
                </motion.div>
              </motion.div>
            </Link>)}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <p className="text-muted-foreground max-w-xl" style={{
          fontSize: "clamp(0.8125rem, 0.88vw, 0.9375rem)",
          letterSpacing: "0.02em",
          lineHeight: 1.45
        }}>
            Cada solução apresentada reflete o nosso compromisso com a inovação, 
            garantindo resultados superiores em todos os setores industriais.
          </p>
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-13 rounded-lg gap-3 pl-7 pr-2" style={{
          height: "52px"
        }} onClick={() => navigate("/ContactsPage")}>
            <span className="text-sm font-medium">Consulte-nos</span>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background text-primary transition-transform duration-300 group-hover:-rotate-45" aria-hidden="true">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </Button>
        </div>
      </div>
    </section>;
}